import type { EcdhKeyPair, SessionMaterials } from '../types/api/session-crypto.types';

export type CryptoMaterials = Pick<SessionMaterials, 'key' | 'counter'>;
export const DECRYPT_ERROR_MESSAGE = 'Failed to decrypt secure payload';
export const MISSING_ENCRYPTED_DATA_MESSAGE = 'Secure API response does not contain encrypted data';

/** Existing upstream wire protocol. No HTTP, storage, session policy or retries live here. */
export async function generateEcdhKeyPair(subtle: SubtleCrypto = crypto.subtle): Promise<EcdhKeyPair>
{
    try
    {
        return await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-521' }, false, [ 'deriveBits' ]) as EcdhKeyPair;
    }
    catch
    {
        return await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-521' }, true, [ 'deriveBits' ]) as EcdhKeyPair;
    }
}

export async function exportPublicKey(publicKey: CryptoKey, subtle: SubtleCrypto = crypto.subtle): Promise<string>
{
    const bytes = new Uint8Array(await subtle.exportKey('raw', publicKey));
    let binary = '';
    for (let index = 0; index < bytes.length; index++) binary += String.fromCharCode(bytes[index]);
    return btoa(binary);
}

/** Preserve the established left-padding/trailing-byte P-521 normalization. */
export function normalizeP521Secret(buffer: ArrayBuffer): Uint8Array
{
    const secret = new Uint8Array(buffer);
    if (secret.length === 66) return secret;
    if (secret.length > 66) return secret.slice(secret.length - 66);
    const padded = new Uint8Array(66);
    padded.set(secret, 66 - secret.length);
    return padded;
}

export async function deriveSessionMaterials(
    privateKey: CryptoKey,
    serverPublicKey: string,
    subtle: SubtleCrypto = crypto.subtle,
): Promise<CryptoMaterials>
{
    if (!serverPublicKey) throw new Error('Server public key is missing in key/set response');
    const binary = atob(serverPublicKey);
    const raw = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) raw[index] = binary.charCodeAt(index);
    const serverKey = await subtle.importKey('raw', raw, { name: 'ECDH', namedCurve: 'P-521' }, true, []);
    const bits = await subtle.deriveBits({ name: 'ECDH', public: serverKey }, privateKey, null as unknown as number);
    const secret = normalizeP521Secret(bits);
    const counter = copyBuffer(secret.slice(0, 16));
    const digest = await subtle.digest('SHA-256', copyBuffer(secret.slice(16, 66)));
    const key = await subtle.importKey('raw', digest, { name: 'AES-CTR', length: 256 }, false, [ 'encrypt', 'decrypt' ]);
    return { key, counter };
}

export async function encryptPayload(
    body: unknown,
    materials: CryptoMaterials,
    subtle: SubtleCrypto = crypto.subtle,
): Promise<string>
{
    if (body == null) return '';
    const plainBytes = new TextEncoder().encode(JSON.stringify(body));
    const encrypted = await subtle.encrypt(
        { name: 'AES-CTR', counter: materials.counter, length: 128 },
        materials.key,
        plainBytes,
    );
    return Array.from(new Uint8Array(encrypted), value => value.toString(16).padStart(2, '0')).join('');
}

export async function decryptPayload<T = Record<string, unknown>>(
    hexData: string | null | undefined,
    materials: CryptoMaterials,
    subtle: SubtleCrypto = crypto.subtle,
): Promise<T>
{
    if (!hexData) return {} as T;
    try
    {
        const normalized = hexData.trim();
        if (normalized.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(normalized))
        {
            throw new Error('Invalid hex payload');
        }
        const encryptedBytes = new Uint8Array(normalized.length / 2);
        for (let index = 0; index < normalized.length; index += 2)
        {
            encryptedBytes[index / 2] = Number.parseInt(normalized.slice(index, index + 2), 16);
        }
        const decrypted = await subtle.decrypt(
            { name: 'AES-CTR', counter: materials.counter, length: 128 },
            materials.key,
            encryptedBytes,
        );
        return JSON.parse(new TextDecoder().decode(decrypted)) as T;
    }
    catch (error)
    {
        throw new Error(DECRYPT_ERROR_MESSAGE, { cause: error });
    }
}

export function encryptedEnvelopeData(response: unknown): string
{
    const data = response && typeof response === 'object' ? (response as { data?: unknown }).data : undefined;
    if (typeof data !== 'string' || data.trim().length === 0)
    {
        throw new Error(MISSING_ENCRYPTED_DATA_MESSAGE);
    }
    return data;
}

function copyBuffer(bytes: Uint8Array): ArrayBuffer
{
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy.buffer;
}
